import { useMemo } from "react";

import { AsyncHandlerOptions, useAsyncResultHandler, useTransactionsState } from "@bridgesplit/react";
import { Button, ButtonProps, ErrorWrapper, SupportedKey, Tooltip, useKeyDetecter } from "@bridgesplit/ui";
import { formatTokenAmount, MIN_SOL_BALANCE, Result, SOL_SYMBOL, WRAPPED_SOL_MINT } from "@bridgesplit/utils";
import { useAppDialog } from "app/utils";
import { TransactionTypes } from "app/types";
import { useLoopscaleWalletContext, useWalletBalanceByMint } from "@bridgesplit/abf-react";

import { useTransactionData } from "../transactions";

interface AsyncCtaOptions {
    options?: AsyncHandlerOptions;
    closeDialog?: "always" | "onSuccess" | "never";
}
interface AsyncCtaOptionsResult<T> extends AsyncCtaOptions {
    onClickWithResult: () => Promise<Result<T>>;
}
interface AsyncCtaOptionsNonResult<T> extends AsyncCtaOptions {
    onClick: () => Promise<T>;
}

export interface AppButtonProps<ResultType> extends ButtonProps {
    handleUnauthorized?: "hide" | "message";
    tooltip?: string;
    asyncCta?: AsyncCtaOptionsResult<ResultType> | AsyncCtaOptionsNonResult<ResultType>;
    syncCta?: () => Result<ResultType>;
    errorMessage?: string;
    keyListener?: SupportedKey;
    isTransaction: boolean;
    transactionType?: TransactionTypes;
    showTransactionBalanceWarning?: boolean;
    overrideAccess?: boolean;
    requiresBeta?: boolean;
    dataTestId?: string;
}
export function AppButton<ResultType>({
    handleUnauthorized = "message",
    tooltip,
    disabled: propsDisabled,
    onClick,
    asyncCta,
    syncCta,
    children,
    keyListener,
    errorMessage,
    fullWidth = true,
    variant = "contained",
    isTransaction,
    transactionType,
    showTransactionBalanceWarning = true,
    overrideAccess = false,
    dataTestId,
    ...props
}: AppButtonProps<ResultType>) {
    const { close } = useAppDialog();
    const { handler, resultHandler, syncHandler, isLoading } = useAsyncResultHandler<ResultType>();
    const { isTransactionSignaturePending } = useTransactionsState();
    const { getTransactionFees } = useTransactionData();
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const { data: solanaBalance, isLoading: isLoadingSolanaBalance } = useWalletBalanceByMint(
        activeWallet,
        WRAPPED_SOL_MINT,
        {
            skip: !activeWallet
        }
    );

    const defaultTransactionType = TransactionTypes.Generic; // replace with your generic default
    const effectiveTransactionType = transactionType ?? defaultTransactionType;

    const transactionsInProgress = isTransaction && isTransactionSignaturePending;

    const insufficientBalance = useMemo(() => {
        if (!isTransaction || isLoadingSolanaBalance) return false;
        return getTransactionFees(effectiveTransactionType).totalFeeAmount > (solanaBalance?.amount ?? 0);
    }, [isTransaction, isLoadingSolanaBalance, getTransactionFees, effectiveTransactionType, solanaBalance?.amount]);

    const buttonTooltip = useMemo(() => {
        if (transactionsInProgress) return "Transaction in progress";
        return tooltip;
    }, [tooltip, transactionsInProgress]);

    const disabled = propsDisabled || props.loading || !!errorMessage || transactionsInProgress;

    useKeyDetecter(keyListener ?? "Enter", handleClick, !keyListener || disabled);

    async function handleClick(e?: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
        e && onClick?.(e);

        if (asyncCta) {
            if (asyncCta.closeDialog === "always") {
                close();
            }

            const options: AsyncHandlerOptions = {
                ...asyncCta.options,
                alertOnError: asyncCta.options?.alertOnError ?? true
            };
            const isResult = "onClick" in asyncCta;
            const getResult = isResult
                ? handler(asyncCta.onClick, options)
                : resultHandler(asyncCta.onClickWithResult, options);
            const result = await getResult;
            if (result.isOk() && asyncCta.closeDialog === "onSuccess") {
                close();
            }
        }
        if (syncCta) {
            syncHandler(syncCta);
        }
    }

    if (handleUnauthorized === "hide") {
        return null;
    }

    const button = (
        <Button
            dataTestId={dataTestId}
            fullWidth={fullWidth}
            width={fullWidth ? undefined : "fit-content"}
            variant={variant}
            loading={props.loading || isLoading}
            onClick={handleClick}
            disabled={disabled}
            {...props}
        >
            {children}
        </Button>
    );

    if (insufficientBalance && showTransactionBalanceWarning) {
        const feeAmount = getTransactionFees(effectiveTransactionType).totalFeeAmount;

        const isGeneric = effectiveTransactionType === TransactionTypes.Generic;
        const errorMessage = isGeneric ? undefined : `Low wallet balance`;
        const tooltipText = getTooltipText(feeAmount, isGeneric);

        return (
            <ErrorWrapper fullWidth={fullWidth} errorMessage={errorMessage} color="warning" tooltipText={tooltipText}>
                {button}
            </ErrorWrapper>
        );
    }

    if (!buttonTooltip) {
        if (!errorMessage) {
            return button;
        }
        return (
            <ErrorWrapper fullWidth={fullWidth} errorMessage={errorMessage}>
                {button}
            </ErrorWrapper>
        );
    }

    return (
        <ErrorWrapper fullWidth={fullWidth} errorMessage={errorMessage}>
            <Tooltip
                fullWidth={fullWidth}
                containerSx={{ cursor: disabled ? "not-allowed" : undefined }}
                title={buttonTooltip}
            >
                {button}
            </Tooltip>
        </ErrorWrapper>
    );
}

function getTooltipText(feeAmount: number, isGeneric: boolean) {
    if (isGeneric) {
        return undefined;
    }
    return `We recommend having at least ${formatTokenAmount(feeAmount + MIN_SOL_BALANCE, {
        symbol: SOL_SYMBOL
    })} to complete this transaction`;
}
